import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Flex,
  SimpleGrid,
  Badge,
  Switch,
  Spinner,
  Alert,
  AlertIcon,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useToast,
  useDisclosure,
  useColorModeValue,
  HStack,
  VStack,
  Icon,
  Collapse,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  ButtonGroup,
  Input,
  InputGroup,
  InputLeftElement,
  Tooltip,
} from '@chakra-ui/react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import {
  FiPlus, FiEdit2, FiZap, FiUsers, FiTrash2, FiActivity, FiSettings,
  FiChevronDown, FiChevronRight, FiGrid, FiList, FiSearch, FiEye, FiCpu,
  FiCopy, FiClock,
} from 'react-icons/fi';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../../contexts/AuthContext';
import { useImpersonation } from '../../../contexts/ImpersonationContext';
import { useTourActions } from '../../../components/FeatureTour/FeatureTourProvider';
import { useCampaignTemplates } from '../../../hooks/useCampaignTemplates';
import { useUserCampaigns } from '../../../hooks/useUserCampaigns';
import { useCrmUsers } from '../../../hooks/useCrmUsers';
import { fetchAutomationRunners, toggleUserCampaign, fetchSubscribersByTemplate } from '../../../api/supabase/automations';
import {
  canEditTemplate as checkCanEditTemplate,
  canViewTemplate as checkCanViewTemplate,
  isPersonalTemplate,
  isCompanyAutomation,
} from '../../../utils/campaignAccess';
import CampaignLogsDrawer from './CampaignLogsDrawer';
import CampaignSettingsDrawer from './CampaignSettingsDrawer';
import TemplateManagementDrawer from './TemplateManagementDrawer';
import DuplicateCampaignModal from './DuplicateCampaignModal';
import VersionHistoryDrawer from './VersionHistoryDrawer';
import RecentlyDeletedSection from './RecentlyDeletedSection';
import ImpersonationBar from '../../../components/shared/ImpersonationBar';

function AdminTemplatesTable({ templates, loading, onDelete, onDuplicate, onHistory, onViewLogs, onSettings, onManage }) {
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const subText = useColorModeValue('gray.500', 'gray.400');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search) return templates;
    const q = search.toLowerCase();
    return templates.filter(
      (t) =>
        (t.name || '').toLowerCase().includes(q) ||
        (t.triggerType || '').toLowerCase().includes(q)
    );
  }, [templates, search]);

  if (loading) {
    return <Flex justify="center" py={10}><Spinner /></Flex>;
  }

  if (!templates.length) {
    return (
      <Text color="gray.500" textAlign="center" py={8}>
        No campaign templates yet. Create your first one!
      </Text>
    );
  }

  return (
    <VStack align="stretch" spacing={3}>
      <InputGroup size="sm" maxW="320px">
        <InputLeftElement pointerEvents="none">
          <FiSearch color="gray" />
        </InputLeftElement>
        <Input
          placeholder="Search templates..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          borderRadius="md"
        />
      </InputGroup>

      <Box overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Trigger</Th>
              <Th isNumeric>Version</Th>
              <Th>Status</Th>
              <Th>Re-enroll</Th>
              <Th isNumeric>Subscribers</Th>
              <Th>Activity</Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {filtered.map((t) => (
              <Tr key={t.id} _hover={{ bg: hoverBg }}>
                <Td>
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="medium" fontSize="sm">{t.name}</Text>
                    {t.description && (
                      <Text fontSize="xs" color={subText} noOfLines={1} maxW="200px">
                        {t.description}
                      </Text>
                    )}
                  </VStack>
                </Td>
                <Td>
                  <Badge colorScheme="purple" fontSize="xs">
                    {(t.triggerType || 'N/A').replace(/_/g, ' ')}
                  </Badge>
                </Td>
                <Td isNumeric>v{t.version || 1}</Td>
                <Td>
                  <Badge colorScheme={t.isActive ? 'green' : 'gray'} fontSize="xs">
                    {t.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </Td>
                <Td>
                  <Badge
                    colorScheme={t.enrollmentRules?.allow_re_enrollment ? 'orange' : 'gray'}
                    fontSize="xs"
                    variant="subtle"
                  >
                    {t.enrollmentRules?.allow_re_enrollment ? 'On' : 'Off'}
                  </Badge>
                </Td>
                <Td isNumeric>
                  <Tooltip label={`${t.activeSubscribers ?? 0} active / ${t.totalSubscribers ?? 0} total`}>
                    <Text fontSize="sm" cursor="default">
                      {t.activeSubscribers ?? 0}
                      <Text as="span" color={subText}> / {t.totalSubscribers ?? 0}</Text>
                    </Text>
                  </Tooltip>
                </Td>
                <Td>
                  <HStack spacing={2} fontSize="xs">
                    <Tooltip label="Pending">
                      <HStack spacing={0.5}>
                        <Box w="6px" h="6px" borderRadius="full" bg="yellow.400" />
                        <Text>{t.pendingQueueItems ?? 0}</Text>
                      </HStack>
                    </Tooltip>
                    <Tooltip label="Completed">
                      <HStack spacing={0.5}>
                        <Box w="6px" h="6px" borderRadius="full" bg="green.400" />
                        <Text>{t.completedQueueItems ?? 0}</Text>
                      </HStack>
                    </Tooltip>
                    <Tooltip label="Failed">
                      <HStack spacing={0.5}>
                        <Box w="6px" h="6px" borderRadius="full" bg="red.400" />
                        <Text>{t.failedQueueItems ?? 0}</Text>
                      </HStack>
                    </Tooltip>
                  </HStack>
                </Td>
                <Td>
                  <HStack spacing={1} justify="flex-end">
                    <Button
                      size="xs"
                      colorScheme="blue"
                      variant="outline"
                      leftIcon={<FiUsers />}
                      onClick={() => onManage(t)}
                    >
                      Manage
                    </Button>
                    <Tooltip label="Settings">
                      <IconButton
                        icon={<FiSettings />}
                        size="xs"
                        variant="ghost"
                        aria-label="Settings"
                        onClick={() => onSettings(t)}
                      />
                    </Tooltip>
                    <Tooltip label="View Logs">
                      <IconButton
                        icon={<FiActivity />}
                        size="xs"
                        variant="ghost"
                        aria-label="View Logs"
                        onClick={() => onViewLogs({ templateId: t.id, name: t.name })}
                      />
                    </Tooltip>
                    <Tooltip label="Edit in builder">
                      <IconButton
                        as={RouterLink}
                        to={`/crm/campaigns/builder/${t.id}`}
                        icon={<FiEdit2 />}
                        size="xs"
                        variant="ghost"
                        aria-label="Edit"
                      />
                    </Tooltip>
                    <Tooltip label="Duplicate">
                      <IconButton
                        icon={<FiCopy />}
                        size="xs"
                        variant="ghost"
                        aria-label="Duplicate"
                        onClick={() => onDuplicate(t)}
                      />
                    </Tooltip>
                    <Tooltip label="Version history">
                      <IconButton
                        icon={<FiClock />}
                        size="xs"
                        variant="ghost"
                        aria-label="Version history"
                        onClick={() => onHistory(t)}
                      />
                    </Tooltip>
                    <Tooltip label="Delete template">
                      <IconButton
                        icon={<FiTrash2 />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        aria-label="Delete"
                        onClick={() => onDelete(t)}
                      />
                    </Tooltip>
                  </HStack>
                </Td>
              </Tr>
            ))}
            {filtered.length === 0 && templates.length > 0 && (
              <Tr>
                <Td colSpan={8} textAlign="center" py={6}>
                  <Text fontSize="sm" color="gray.500">No templates match "{search}"</Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    </VStack>
  );
}

/**
 * Admin-only table of Company Automations — hidden, company-wide campaigns
 * (execution_scope='company') that fire on any owner's deals/contacts through
 * a single "runner" subscription. The switch toggles the runner's is_enabled;
 * there is no subscriber management and no visibility switch (always hidden).
 */
function CompanyAutomationsSection({ templates, loading, onViewLogs, onDelete, onDuplicate }) {
  const toast = useToast();
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const subText = useColorModeValue('gray.500', 'gray.400');

  // templateId -> runner subscription row (snake_case from the data layer)
  const [runners, setRunners] = useState({});
  const [runnersLoading, setRunnersLoading] = useState(false);
  const [busyTemplateId, setBusyTemplateId] = useState(null);

  const templateIdsKey = templates.map((t) => t.id).sort().join(',');

  const loadRunners = useCallback(async () => {
    const ids = templateIdsKey ? templateIdsKey.split(',') : [];
    if (!ids.length) {
      setRunners({});
      return;
    }
    setRunnersLoading(true);
    try {
      const rows = await fetchAutomationRunners(ids);
      const map = {};
      rows.forEach((r) => { map[r.template_id] = r; });
      setRunners(map);
    } catch (err) {
      console.error('[CompanyAutomationsSection] runner load error:', err);
    } finally {
      setRunnersLoading(false);
    }
  }, [templateIdsKey]);

  useEffect(() => { loadRunners(); }, [loadRunners]);

  const handleToggle = async (template, enabled) => {
    const runner = runners[template.id];
    if (!runner) return;
    setBusyTemplateId(template.id);
    try {
      const updated = await toggleUserCampaign(runner.id, enabled);
      setRunners((prev) => ({
        ...prev,
        [template.id]: { ...runner, is_enabled: updated?.is_enabled ?? enabled },
      }));
      toast({
        title: enabled ? 'Automation enabled' : 'Automation disabled',
        status: enabled ? 'success' : 'info',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({ title: 'Toggle failed', description: err.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setBusyTemplateId(null);
    }
  };

  // Repair path: recreates the hidden runner subscription (cloning the current
  // template nodes) if it's missing — e.g. deleted directly in the database.
  const handleInitRunner = async (template) => {
    setBusyTemplateId(template.id);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-east1'), 'subscribeToCampaign');
      await fn({ templateId: template.id });
      await loadRunners();
      toast({ title: 'Runner created (disabled)', status: 'success', duration: 3000, isClosable: true });
    } catch (err) {
      toast({ title: 'Failed to create runner', description: err.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setBusyTemplateId(null);
    }
  };

  if (loading) {
    return <Flex justify="center" py={10}><Spinner /></Flex>;
  }

  if (!templates.length) {
    return (
      <Text color="gray.500" textAlign="center" py={8} fontSize="sm">
        No company automations yet. Create one from the builder by choosing the
        &ldquo;Automation&rdquo; campaign type — it will run on every user&apos;s deals and
        contacts while staying invisible to non-admins.
      </Text>
    );
  }

  return (
    <Box overflowX="auto">
      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th>Trigger</Th>
            <Th>Status</Th>
            <Th>Enabled</Th>
            <Th>Activity</Th>
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {templates.map((t) => {
            const runner = runners[t.id];
            const busy = busyTemplateId === t.id;
            return (
              <Tr key={t.id} _hover={{ bg: hoverBg }}>
                <Td>
                  <VStack align="start" spacing={0}>
                    <HStack spacing={2}>
                      <Icon as={FiCpu} boxSize={3.5} color="purple.400" />
                      <Text fontWeight="medium" fontSize="sm">{t.name}</Text>
                    </HStack>
                    {t.description && (
                      <Text fontSize="xs" color={subText} noOfLines={1} maxW="240px">
                        {t.description}
                      </Text>
                    )}
                  </VStack>
                </Td>
                <Td>
                  <Badge colorScheme="purple" fontSize="xs">
                    {(t.triggerType || 'N/A').replace(/_/g, ' ')}
                  </Badge>
                </Td>
                <Td>
                  {runnersLoading && !runner ? (
                    <Spinner size="xs" />
                  ) : runner ? (
                    <Badge colorScheme={runner.is_enabled ? 'green' : 'gray'} fontSize="xs">
                      {runner.is_enabled ? 'Running' : 'Off'}
                    </Badge>
                  ) : (
                    <Button
                      size="xs"
                      variant="outline"
                      colorScheme="purple"
                      isLoading={busy}
                      onClick={() => handleInitRunner(t)}
                    >
                      Create runner
                    </Button>
                  )}
                </Td>
                <Td>
                  <Switch
                    size="sm"
                    colorScheme="green"
                    isChecked={!!runner?.is_enabled}
                    isDisabled={!runner || busy}
                    onChange={(e) => handleToggle(t, e.target.checked)}
                  />
                </Td>
                <Td>
                  <HStack spacing={2} fontSize="xs">
                    <Tooltip label="Pending">
                      <HStack spacing={0.5}>
                        <Box w="6px" h="6px" borderRadius="full" bg="yellow.400" />
                        <Text>{t.pendingQueueItems ?? 0}</Text>
                      </HStack>
                    </Tooltip>
                    <Tooltip label="Completed">
                      <HStack spacing={0.5}>
                        <Box w="6px" h="6px" borderRadius="full" bg="green.400" />
                        <Text>{t.completedQueueItems ?? 0}</Text>
                      </HStack>
                    </Tooltip>
                    <Tooltip label="Failed">
                      <HStack spacing={0.5}>
                        <Box w="6px" h="6px" borderRadius="full" bg="red.400" />
                        <Text>{t.failedQueueItems ?? 0}</Text>
                      </HStack>
                    </Tooltip>
                  </HStack>
                </Td>
                <Td>
                  <HStack spacing={1} justify="flex-end">
                    <Tooltip label="View Logs">
                      <IconButton
                        icon={<FiActivity />}
                        size="xs"
                        variant="ghost"
                        aria-label="View Logs"
                        onClick={() => onViewLogs({ templateId: t.id, name: t.name })}
                      />
                    </Tooltip>
                    <Tooltip label="Edit in builder">
                      <IconButton
                        as={RouterLink}
                        to={`/crm/campaigns/builder/${t.id}`}
                        icon={<FiEdit2 />}
                        size="xs"
                        variant="ghost"
                        aria-label="Edit"
                      />
                    </Tooltip>
                    <Tooltip label="Duplicate">
                      <IconButton
                        icon={<FiCopy />}
                        size="xs"
                        variant="ghost"
                        aria-label="Duplicate"
                        onClick={() => onDuplicate(t)}
                      />
                    </Tooltip>
                    <Tooltip label="Delete automation">
                      <IconButton
                        icon={<FiTrash2 />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        aria-label="Delete"
                        onClick={() => onDelete(t)}
                      />
                    </Tooltip>
                  </HStack>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
}

function UserCampaignCards({ campaigns, loading, togglingId, onToggle, onUnsubscribe, onViewLogs, onSettings, onDelete, onDuplicate, canEditTemplate, canDuplicateTemplate }) {
  const cardBg = useColorModeValue('white', 'gray.700');
  const border = useColorModeValue('gray.200', 'gray.600');

  if (loading) {
    return <Flex justify="center" py={10}><Spinner /></Flex>;
  }

  if (!campaigns.length) {
    return (
      <Text color="gray.500" textAlign="center" py={8}>
        No campaigns activated yet. Browse the available campaigns below.
      </Text>
    );
  }

  return (
    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
      {campaigns.map((c) => {
        const editable = canEditTemplate(c.template);
        return (
          <Box
            key={c.id}
            bg={cardBg}
            border="1px solid"
            borderColor={border}
            borderRadius="lg"
            p={4}
          >
            <Flex justify="space-between" align="start" mb={2}>
              <VStack align="start" spacing={0}>
                <HStack spacing={1}>
                  <Text fontWeight="bold" fontSize="sm">
                    {c.template?.name || 'Campaign'}
                  </Text>
                  {c.template?.enrollmentRules?.allow_re_enrollment && (
                    <Badge colorScheme="orange" fontSize="2xs" variant="subtle">Auto</Badge>
                  )}
                  {c.template?.ownershipType !== 'user' && (
                    <Badge colorScheme="blue" fontSize="2xs" variant="subtle">Company</Badge>
                  )}
                </HStack>
                <Text fontSize="xs" color="gray.500">
                  v{c.templateVersion || 1}
                </Text>
              </VStack>
              <Switch
                colorScheme="green"
                isChecked={c.isEnabled}
                isDisabled={togglingId === c.id}
                onChange={() => onToggle(c.id, !c.isEnabled)}
              />
            </Flex>
            <Text fontSize="xs" color="gray.500" noOfLines={2} mb={3}>
              {c.template?.description || 'No description'}
            </Text>
            <HStack spacing={2} flexWrap="wrap">
              <Button
                size="xs"
                variant="ghost"
                leftIcon={<FiActivity />}
                onClick={() => onViewLogs({ userCampaignId: c.id, name: c.template?.name || 'Campaign' })}
              >
                Logs
              </Button>
              <IconButton
                icon={<FiSettings />}
                size="xs"
                variant="ghost"
                aria-label="Settings"
                onClick={() => onSettings(c)}
              />
              <IconButton
                as={RouterLink}
                to={`/crm/campaigns/view/${c.templateId}`}
                icon={<FiEye />}
                size="xs"
                variant="ghost"
                aria-label="View campaign"
              />
              {canDuplicateTemplate(c.template) && (
                <IconButton
                  icon={<FiCopy />}
                  size="xs"
                  variant="ghost"
                  aria-label="Duplicate campaign"
                  onClick={() => onDuplicate(c.template)}
                />
              )}
              {editable && (
                <IconButton
                  as={RouterLink}
                  to={`/crm/campaigns/builder/${c.templateId}`}
                  icon={<FiEdit2 />}
                  size="xs"
                  variant="ghost"
                  aria-label="Edit campaign"
                />
              )}
              {editable && (
                <IconButton
                  icon={<FiTrash2 />}
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  aria-label="Delete campaign"
                  onClick={() => onDelete(c.template)}
                />
              )}
              <Button
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={() => onUnsubscribe(c.id)}
              >
                Unsubscribe
              </Button>
            </HStack>
          </Box>
        );
      })}
    </SimpleGrid>
  );
}

function AvailableCampaignCards({ templates, subscribedTemplateIds, loading, onSubscribe, onDelete, onDuplicate, canEditTemplate, canDuplicateTemplate, isAdmin }) {
  const cardBg = useColorModeValue('white', 'gray.700');
  const border = useColorModeValue('gray.200', 'gray.600');

  // Own personal templates always show; is_visible only hides company
  // templates from non-admins.
  const available = templates.filter(
    (t) => !subscribedTemplateIds.has(t.id) && (isAdmin || isPersonalTemplate(t) || t.isVisible !== false)
  );

  if (loading) {
    return <Flex justify="center" py={10}><Spinner /></Flex>;
  }

  if (!available.length) {
    return (
      <Text color="gray.500" textAlign="center" py={6} fontSize="sm">
        You're subscribed to all available campaigns.
      </Text>
    );
  }

  return (
    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
      {available.map((t) => (
        <Box
          key={t.id}
          bg={cardBg}
          border="1px solid"
          borderColor={border}
          borderRadius="lg"
          p={4}
        >
          <Flex align="center" gap={2} mb={2} justify="space-between">
            <HStack spacing={2}>
              <Icon as={FiZap} color="purple.400" />
              <Text fontWeight="bold" fontSize="sm">{t.name}</Text>
              {isPersonalTemplate(t) && (
                <Badge colorScheme="gray" fontSize="2xs" variant="subtle">Personal</Badge>
              )}
            </HStack>
            <HStack spacing={0}>
              {canEditTemplate(t) && (
                <IconButton
                  as={RouterLink}
                  to={`/crm/campaigns/builder/${t.id}`}
                  icon={<FiEdit2 />}
                  size="xs"
                  variant="ghost"
                  aria-label="Edit campaign"
                />
              )}
              {canDuplicateTemplate(t) && (
                <IconButton
                  icon={<FiCopy />}
                  size="xs"
                  variant="ghost"
                  aria-label="Duplicate campaign"
                  onClick={() => onDuplicate(t)}
                />
              )}
              {canEditTemplate(t) && (
                <IconButton
                  icon={<FiTrash2 />}
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  aria-label="Delete campaign"
                  onClick={() => onDelete(t)}
                />
              )}
            </HStack>
          </Flex>
          <Text fontSize="xs" color="gray.500" noOfLines={2} mb={3}>
            {t.description || 'No description'}
          </Text>
          <Badge colorScheme="purple" fontSize="xs" mb={3}>
            {(t.triggerType || '').replace(/_/g, ' ')}
          </Badge>
          <Button
            size="sm"
            colorScheme="blue"
            w="100%"
            onClick={() => onSubscribe(t.id)}
          >
            Activate
          </Button>
        </Box>
      ))}
    </SimpleGrid>
  );
}

function SectionHeader({ title, count, isOpen, onToggle, viewMode, onViewModeChange }) {
  return (
    <Flex align="center" justify="space-between" mb={isOpen ? 3 : 0} cursor="pointer" userSelect="none">
      <HStack spacing={2} onClick={onToggle} flex={1} py={1}>
        <Icon as={isOpen ? FiChevronDown : FiChevronRight} boxSize={4} color="gray.500" />
        <Heading size="sm">{title}</Heading>
        {typeof count === 'number' && (
          <Badge colorScheme="gray" fontSize="xs" borderRadius="full" px={2}>
            {count}
          </Badge>
        )}
      </HStack>
      <ButtonGroup size="xs" variant="ghost" isAttached>
        <IconButton
          icon={<FiGrid />}
          aria-label="Card view"
          color={viewMode === 'cards' ? 'blue.500' : 'gray.400'}
          onClick={() => onViewModeChange('cards')}
        />
        <IconButton
          icon={<FiList />}
          aria-label="Table view"
          color={viewMode === 'table' ? 'blue.500' : 'gray.400'}
          onClick={() => onViewModeChange('table')}
        />
      </ButtonGroup>
    </Flex>
  );
}

function UserCampaignTable({ campaigns, loading, togglingId, onToggle, onUnsubscribe, onViewLogs, onSettings, onDelete, onDuplicate, canEditTemplate, canDuplicateTemplate }) {
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  if (loading) {
    return <Flex justify="center" py={10}><Spinner /></Flex>;
  }

  if (!campaigns.length) {
    return (
      <Text color="gray.500" textAlign="center" py={8}>
        No campaigns activated yet. Browse the available campaigns below.
      </Text>
    );
  }

  return (
    <Box overflowX="auto">
      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            <Th>Campaign</Th>
            <Th>Description</Th>
            <Th>Enabled</Th>
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {campaigns.map((c) => {
            const editable = canEditTemplate(c.template);
            return (
              <Tr key={c.id} _hover={{ bg: hoverBg }}>
                <Td>
                  <HStack spacing={1}>
                    <Text fontWeight="medium" fontSize="sm">{c.template?.name || 'Campaign'}</Text>
                    {c.template?.enrollmentRules?.allow_re_enrollment && (
                      <Badge colorScheme="orange" fontSize="2xs" variant="subtle">Auto</Badge>
                    )}
                    {c.template?.ownershipType !== 'user' && (
                      <Badge colorScheme="blue" fontSize="2xs" variant="subtle">Company</Badge>
                    )}
                    <Text fontSize="xs" color="gray.500">v{c.templateVersion || 1}</Text>
                  </HStack>
                </Td>
                <Td>
                  <Text fontSize="xs" color="gray.500" noOfLines={1} maxW="260px">
                    {c.template?.description || 'No description'}
                  </Text>
                </Td>
                <Td>
                  <Switch
                    colorScheme="green"
                    size="sm"
                    isChecked={c.isEnabled}
                    isDisabled={togglingId === c.id}
                    onChange={() => onToggle(c.id, !c.isEnabled)}
                  />
                </Td>
                <Td>
                  <HStack spacing={1} justify="flex-end">
                    <Button size="xs" variant="ghost" leftIcon={<FiActivity />} onClick={() => onViewLogs({ userCampaignId: c.id, name: c.template?.name || 'Campaign' })}>
                      Logs
                    </Button>
                    <IconButton icon={<FiSettings />} size="xs" variant="ghost" aria-label="Settings" onClick={() => onSettings(c)} />
                    <IconButton as={RouterLink} to={`/crm/campaigns/view/${c.templateId}`} icon={<FiEye />} size="xs" variant="ghost" aria-label="View" />
                    {canDuplicateTemplate(c.template) && (
                      <IconButton icon={<FiCopy />} size="xs" variant="ghost" aria-label="Duplicate" onClick={() => onDuplicate(c.template)} />
                    )}
                    {editable && (
                      <IconButton as={RouterLink} to={`/crm/campaigns/builder/${c.templateId}`} icon={<FiEdit2 />} size="xs" variant="ghost" aria-label="Edit" />
                    )}
                    {editable && (
                      <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red" aria-label="Delete" onClick={() => onDelete(c.template)} />
                    )}
                    <Button size="xs" variant="ghost" colorScheme="red" onClick={() => onUnsubscribe(c.id)}>
                      Unsubscribe
                    </Button>
                  </HStack>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
}

function AvailableCampaignTable({ templates, subscribedTemplateIds, loading, onSubscribe, onDelete, onDuplicate, canEditTemplate, canDuplicateTemplate, isAdmin }) {
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const available = templates.filter(
    (t) => !subscribedTemplateIds.has(t.id) && (isAdmin || isPersonalTemplate(t) || t.isVisible !== false)
  );

  if (loading) {
    return <Flex justify="center" py={10}><Spinner /></Flex>;
  }

  if (!available.length) {
    return (
      <Text color="gray.500" textAlign="center" py={6} fontSize="sm">
        You're subscribed to all available campaigns.
      </Text>
    );
  }

  return (
    <Box overflowX="auto">
      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            <Th>Campaign</Th>
            <Th>Description</Th>
            <Th>Trigger</Th>
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {available.map((t) => (
            <Tr key={t.id} _hover={{ bg: hoverBg }}>
              <Td>
                <HStack spacing={2}>
                  <Icon as={FiZap} color="purple.400" boxSize={3.5} />
                  <Text fontWeight="medium" fontSize="sm">{t.name}</Text>
                  {isPersonalTemplate(t) && (
                    <Badge colorScheme="gray" fontSize="2xs" variant="subtle">Personal</Badge>
                  )}
                </HStack>
              </Td>
              <Td>
                <Text fontSize="xs" color="gray.500" noOfLines={1} maxW="260px">
                  {t.description || 'No description'}
                </Text>
              </Td>
              <Td>
                <Badge colorScheme="purple" fontSize="xs">
                  {(t.triggerType || '').replace(/_/g, ' ')}
                </Badge>
              </Td>
              <Td>
                <HStack spacing={1} justify="flex-end">
                  {canEditTemplate(t) && (
                    <IconButton as={RouterLink} to={`/crm/campaigns/builder/${t.id}`} icon={<FiEdit2 />} size="xs" variant="ghost" aria-label="Edit" />
                  )}
                  {canDuplicateTemplate(t) && (
                    <IconButton icon={<FiCopy />} size="xs" variant="ghost" aria-label="Duplicate" onClick={() => onDuplicate(t)} />
                  )}
                  {canEditTemplate(t) && (
                    <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red" aria-label="Delete" onClick={() => onDelete(t)} />
                  )}
                  <Button size="sm" colorScheme="blue" onClick={() => onSubscribe(t.id)}>
                    Activate
                  </Button>
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

export default function CampaignsPage() {
  const { currentUser } = useAuth();
  const {
    effectiveUserId,
    effectiveIsAdmin,
    isImpersonating,
    effectiveUserName,
    canImpersonate,
  } = useImpersonation();
  const { users: crmUsers, loading: crmUsersLoading, getUserName } = useCrmUsers();
  const toast = useToast();
  const history = useHistory();
  const logsDrawer = useDisclosure();
  const settingsDrawer = useDisclosure();
  const manageDrawer = useDisclosure();
  const [logsTarget, setLogsTarget] = useState({});
  const [settingsTarget, setSettingsTarget] = useState(null);
  const [manageTarget, setManageTarget] = useState(null);
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  // Delete confirmation: template pending deletion + its live subscriber rows
  // (null while the count loads)
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSubscribers, setDeleteSubscribers] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const deleteCancelRef = useRef();

  // FeatureTour: named actions the campaigns tours can invoke. The Manage
  // Templates tab only exists for admins; the click no-ops for everyone else.
  useTourActions(useMemo(() => {
    const clickTab = (id) => document.querySelector(`[data-tour="${id}"]`)?.click();
    return {
      showMyCampaignsTab: () => clickTab('camp-tab-my'),
      showManageTab: () => clickTab('camp-tab-manage'),
    };
  }, []));

  // Manage Templates tab: company templates only — admins' own personal
  // campaigns live in My Campaigns like any other user's.
  const {
    templates: adminTemplates,
    loading: adminLoading,
    error: adminError,
    remove: removeTemplate,
    reload: reloadAdminTemplates,
  } = useCampaignTemplates({ activeOnly: false, withStats: effectiveIsAdmin, companyOnly: true });

  // Available list: company templates + the effective user's personal ones.
  // While impersonating, this surfaces the impersonated user's personal
  // campaigns (and refetches when the impersonation target changes).
  const {
    templates: activeTemplates,
    loading: activeLoading,
    reload: reloadActiveTemplates,
  } = useCampaignTemplates({ activeOnly: true, forUserId: effectiveUserId });

  const {
    campaigns: userCampaigns,
    loading: userLoading,
    togglingId,
    subscribe,
    toggle,
    unsubscribe,
  } = useUserCampaigns(effectiveUserId);

  // Manage tab split: regular company templates vs Company Automations
  // (execution_scope='company'), which get their own admin table.
  const adminCampaignTemplates = useMemo(
    () => adminTemplates.filter((t) => !isCompanyAutomation(t)),
    [adminTemplates]
  );
  const automationTemplates = useMemo(
    () => adminTemplates.filter((t) => isCompanyAutomation(t)),
    [adminTemplates]
  );

  // Belt and braces: the fetch already excludes Company Automations from the
  // forUserId path, but never let one slip into user-facing lists.
  const visibleActiveTemplates = useMemo(
    () => activeTemplates.filter((t) => !isCompanyAutomation(t)),
    [activeTemplates]
  );

  const subscribedTemplateIds = new Set(userCampaigns.map((c) => c.templateId));
  const availableCount = visibleActiveTemplates.filter(
    (t) => !subscribedTemplateIds.has(t.id) && (effectiveIsAdmin || isPersonalTemplate(t) || t.isVisible !== false)
  ).length;

  const [myOpen, setMyOpen] = useState(true);
  const [availOpen, setAvailOpen] = useState(true);
  const [myView, setMyView] = useState('cards');
  const [availView, setAvailView] = useState('cards');

  const handleSubscribe = async (templateId) => {
    try {
      await subscribe(templateId);
      toast({ title: 'Campaign activated', status: 'success', duration: 3000, isClosable: true });
    } catch (err) {
      toast({ title: 'Failed to activate', description: err.message, status: 'error', duration: 5000, isClosable: true });
    }
  };

  const handleToggle = async (id, enabled) => {
    try {
      await toggle(id, enabled);
    } catch (err) {
      toast({ title: 'Toggle failed', description: err.message, status: 'error', duration: 3000, isClosable: true });
    }
  };

  const handleUnsubscribe = async (id) => {
    try {
      await unsubscribe(id);
      toast({ title: 'Unsubscribed', status: 'info', duration: 3000, isClosable: true });
    } catch (err) {
      toast({ title: 'Failed', description: err.message, status: 'error', duration: 3000, isClosable: true });
    }
  };

  const handleViewLogs = ({ userCampaignId, templateId, name }) => {
    setLogsTarget({ userCampaignId, templateId, name });
    logsDrawer.onOpen();
  };

  const handleSettings = (campaignOrTemplate) => {
    // Normalize: admin template objects are top-level, user campaign objects have .template nested
    if (campaignOrTemplate.template) {
      setSettingsTarget(campaignOrTemplate);
    } else {
      setSettingsTarget({ template: campaignOrTemplate, isEnabled: campaignOrTemplate.isActive });
    }
    settingsDrawer.onOpen();
  };

  // Step 1 of deletion: open the confirmation dialog and fetch the live
  // subscriber count so the admin sees the blast radius before confirming.
  const handleDeleteTemplate = (template) => {
    if (!template?.id) return;
    setDeleteTarget(template);
    setDeleteSubscribers(null);
    fetchSubscribersByTemplate(template.id)
      .then((rows) => setDeleteSubscribers(rows || []))
      .catch(() => setDeleteSubscribers([]));
  };

  // Step 2: confirmed. The RPC deletes subscriptions + template atomically and
  // preserves the final snapshot (with subscriber list + deleted_by) in
  // version history, so this is recoverable from Recently Deleted.
  const confirmDeleteTemplate = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await removeTemplate(deleteTarget.id, currentUser?.uid);
      // The Available section renders from a separate hook instance; My
      // Campaigns refreshes itself via its realtime channel.
      reloadActiveTemplates();
      toast({
        title: 'Campaign deleted',
        description: 'Admins can restore it from Recently Deleted (Manage Templates).',
        status: 'info',
        duration: 4000,
        isClosable: true,
      });
      setDeleteTarget(null);
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, status: 'error', duration: 3000, isClosable: true });
    } finally {
      setDeleting(false);
    }
  };

  const handleManage = (template) => {
    setManageTarget(template);
    manageDrawer.onOpen();
  };

  const handleDuplicate = (template) => {
    if (template?.id) setDuplicateTarget(template);
  };

  const handleDuplicated = (created, { ownedByMe } = {}) => {
    reloadAdminTemplates();
    reloadActiveTemplates();
    if (ownedByMe && created?.id) {
      history.push(`/crm/campaigns/builder/${created.id}`);
    }
  };

  const handleHistory = (template) => {
    if (template?.id) setHistoryTarget(template);
  };

  const handleRestored = () => {
    reloadAdminTemplates();
    reloadActiveTemplates();
  };

  // realAdmin (canImpersonate) rather than effectiveIsAdmin: an admin
  // impersonating a user keeps edit access to that user's personal campaigns.
  const canEditTemplate = (template) =>
    checkCanEditTemplate(template, { uid: currentUser?.uid, realAdmin: canImpersonate });

  // Duplicate requires view access only (the copy belongs to the duplicator).
  // The lists already filter to viewable templates; this guards odd states.
  const canDuplicateTemplate = (template) =>
    checkCanViewTemplate(template, {
      uid: currentUser?.uid,
      realAdmin: canImpersonate,
      impersonatedUid: isImpersonating ? effectiveUserId : null,
    });

  const userCampaignProps = {
    campaigns: userCampaigns,
    loading: userLoading,
    togglingId,
    onToggle: handleToggle,
    onUnsubscribe: handleUnsubscribe,
    onViewLogs: handleViewLogs,
    onSettings: handleSettings,
    onDelete: handleDeleteTemplate,
    onDuplicate: handleDuplicate,
    canEditTemplate,
    canDuplicateTemplate,
  };

  const availableCampaignProps = {
    templates: visibleActiveTemplates,
    subscribedTemplateIds,
    loading: activeLoading,
    onSubscribe: handleSubscribe,
    onDelete: handleDeleteTemplate,
    onDuplicate: handleDuplicate,
    canEditTemplate,
    canDuplicateTemplate,
    isAdmin: effectiveIsAdmin,
  };

  const myCampaignsContent = (
    <>
      <Box mb={6} data-tour="camp-my-section">
        <SectionHeader
          title={isImpersonating ? `${effectiveUserName}'s Campaigns` : 'My Campaigns'}
          count={userCampaigns.length}
          isOpen={myOpen}
          onToggle={() => setMyOpen(!myOpen)}
          viewMode={myView}
          onViewModeChange={setMyView}
        />
        <Collapse in={myOpen} animateOpacity>
          {myView === 'cards'
            ? <UserCampaignCards {...userCampaignProps} />
            : <UserCampaignTable {...userCampaignProps} />}
        </Collapse>
      </Box>

      <Box data-tour="camp-available-section">
        <SectionHeader
          title="Available Campaigns"
          count={availableCount}
          isOpen={availOpen}
          onToggle={() => setAvailOpen(!availOpen)}
          viewMode={availView}
          onViewModeChange={setAvailView}
        />
        <Collapse in={availOpen} animateOpacity>
          {availView === 'cards'
            ? <AvailableCampaignCards {...availableCampaignProps} />
            : <AvailableCampaignTable {...availableCampaignProps} />}
        </Collapse>
      </Box>
    </>
  );

  return (
    <Container maxW="container.xl" py={6}>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="lg" data-tour="camp-header">Campaigns</Heading>
        <Button
          as={RouterLink}
          to="/crm/campaigns/builder"
          leftIcon={<FiPlus />}
          colorScheme="blue"
          size="sm"
          data-tour="camp-new"
        >
          New Campaign
        </Button>
      </Flex>

      {canImpersonate && (
        <ImpersonationBar
          users={crmUsers}
          loading={crmUsersLoading}
          label="View as user"
        />
      )}

      {adminError && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          {adminError}
        </Alert>
      )}

      {effectiveIsAdmin ? (
        <Tabs colorScheme="blue" variant="line" isLazy>
          <TabList mb={6}>
            <Tab data-tour="camp-tab-my">My Campaigns</Tab>
            <Tab data-tour="camp-tab-manage">
              <HStack spacing={2}>
                <Icon as={FiUsers} boxSize={3.5} />
                <Text>Manage Templates</Text>
              </HStack>
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0} pt={0}>
              {myCampaignsContent}
            </TabPanel>
            <TabPanel px={0} pt={0}>
              <Box data-tour="camp-admin-table">
                <AdminTemplatesTable
                  templates={adminCampaignTemplates}
                  loading={adminLoading}
                  onDelete={handleDeleteTemplate}
                  onDuplicate={handleDuplicate}
                  onHistory={handleHistory}
                  onViewLogs={handleViewLogs}
                  onSettings={handleSettings}
                  onManage={handleManage}
                />
              </Box>

              <Box mt={10}>
                <HStack spacing={2} mb={1}>
                  <Icon as={FiCpu} boxSize={4} color="purple.400" />
                  <Heading size="sm">Company Automations</Heading>
                  {automationTemplates.length > 0 && (
                    <Badge colorScheme="purple" variant="subtle" borderRadius="full">
                      {automationTemplates.length}
                    </Badge>
                  )}
                </HStack>
                <Text fontSize="xs" color="gray.500" mb={3}>
                  Backend campaigns that fire on every user&apos;s deals and contacts,
                  regardless of owner. Invisible to non-admins and never shown as
                  enrollments on deals or contacts.
                </Text>
                <CompanyAutomationsSection
                  templates={automationTemplates}
                  loading={adminLoading}
                  onViewLogs={handleViewLogs}
                  onDelete={handleDeleteTemplate}
                  onDuplicate={handleDuplicate}
                />
              </Box>

              {canImpersonate && (
                <Box mt={10}>
                  <RecentlyDeletedSection
                    getUserName={getUserName}
                    onRestored={handleRestored}
                  />
                </Box>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      ) : (
        myCampaignsContent
      )}

      <CampaignLogsDrawer
        isOpen={logsDrawer.isOpen}
        onClose={logsDrawer.onClose}
        userCampaignId={logsTarget.userCampaignId}
        templateId={logsTarget.templateId}
        campaignName={logsTarget.name}
      />

      <CampaignSettingsDrawer
        isOpen={settingsDrawer.isOpen}
        onClose={settingsDrawer.onClose}
        campaign={settingsTarget}
        canEdit={settingsTarget ? canEditTemplate(settingsTarget.template || settingsTarget) : false}
        isAdmin={effectiveIsAdmin}
        onUpdated={() => reloadAdminTemplates()}
        onShowHistory={handleHistory}
      />

      <TemplateManagementDrawer
        isOpen={manageDrawer.isOpen}
        onClose={manageDrawer.onClose}
        template={manageTarget}
        onViewLogs={handleViewLogs}
        onSettings={handleSettings}
      />

      {/* Mounted only while open so the CRM-user list inside is fetched lazily */}
      {duplicateTarget && (
        <DuplicateCampaignModal
          isOpen
          onClose={() => setDuplicateTarget(null)}
          template={duplicateTarget}
          onDuplicated={handleDuplicated}
        />
      )}

      <VersionHistoryDrawer
        isOpen={!!historyTarget}
        onClose={() => setHistoryTarget(null)}
        template={historyTarget}
        onRestored={handleRestored}
      />

      {/* Delete confirmation — every delete button on this page funnels here */}
      <AlertDialog
        isOpen={!!deleteTarget}
        leastDestructiveRef={deleteCancelRef}
        onClose={() => !deleting && setDeleteTarget(null)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete &ldquo;{deleteTarget?.name}&rdquo;?
            </AlertDialogHeader>
            <AlertDialogBody>
              {deleteSubscribers === null ? (
                <HStack spacing={2}>
                  <Spinner size="sm" />
                  <Text fontSize="sm" color="gray.500">Checking subscribers…</Text>
                </HStack>
              ) : (
                <VStack align="start" spacing={2}>
                  <Text fontSize="sm">
                    {deleteSubscribers.length > 0
                      ? `This removes the campaign for ${deleteSubscribers.length} subscribed user${deleteSubscribers.length === 1 ? '' : 's'} and cancels their pending steps.`
                      : 'Nobody is currently subscribed to this campaign.'}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Admins can restore it later from Recently Deleted on the Manage
                    Templates tab.
                  </Text>
                </VStack>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={deleteCancelRef} size="sm" onClick={() => setDeleteTarget(null)} isDisabled={deleting}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                size="sm"
                ml={3}
                leftIcon={<FiTrash2 />}
                onClick={confirmDeleteTemplate}
                isLoading={deleting}
                isDisabled={deleteSubscribers === null}
              >
                Delete campaign
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
}
